<script lang="ts">
export default {
  name: 'CrimeBenchmarking',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch, reactive } from 'vue';
import AccountBenchmarkViewOptions from '@/components/AccountBenchmarkViewOptions.vue';
import ClientFilter from '@/components/ClientFilter.vue';
import { benchmarkService, referenceService } from '@/services/api';
import { Account } from '@/types/Account';
import { AccountBenchmark, Benchmark, CrimeBenchmark } from '@/types/Benchmark';
import { AttributeOption, both, calc, Chart, Column, PUBLIC, sameOwnershipType, SHOW_ALL } from './Benchmarking';
import BenchmarkingCharts from './BenchmarkingCharts.vue';
import BenchmarkingTable from './BenchmarkingTable.vue';
import { calcAverage, calcMedian } from '@/utilities/Number';
import GenericFilter from '@/components/GenericFilter.vue';
import FilterSlider from '@/components/FilterSlider.vue';
import { useRoute } from 'vue-router';
import { compactMoney } from '@/utilities/Money';
import OwnershipTypeFilter from '@/components/OwnershipTypeFilter.vue';
import { LabelFilterType } from '@/types/components/FilterType';

const $route = useRoute();

const props = defineProps<{
  account: Account;
}>();

const emit = defineEmits<{
  'needs-update': [];
}>();

// -----
// data
// -----
const isLoading = ref<boolean>(false);
const data = ref<CrimeBenchmark[]>([]);
const selectedClients = ref<string[]>([]);
const appliedClients = ref<string[]>([]);
const appliedLabelDisplay = ref<string>(SHOW_ALL);
const employeeCountRange = ref<[number, number]>();
const annualRevenueRange = ref<[number, number]>();
const attributeTypes = ref<AttributeOption[]>([]);
const selectedAttributeTypes = ref<number[]>([]);
const industries = ref<LabelFilterType<number>[]>([]);
const selectedIndustries = ref<number[]>([]);
const accountInfo = ref<AccountBenchmark>({} as AccountBenchmark);
const benchmarkTableKey = ref(0);

const ownershipTypeFilter = reactive({
  showPrivate: false,
  showPublic: false,
});

// ---------
// computed
// ---------
const clients = computed(() => {
  const clients = data.value.reduce((acc, d) => {
    if (d.accountId === $route.params.id) return acc; // exclude current account
    if (!d.accountName) return acc;

    acc.set(d.accountName.toLowerCase(), { text: d.accountName, value: d.accountName });
    return acc;
  }, new Map<string, { text: string, value: string }>());

  return Array.from(clients.values()).sort((a, b) => a.text.localeCompare(b.text));
});

const accountData = computed<CrimeBenchmark[]>(() => {
  const maxRecord = data.value
    .filter(d => d.accountId === $route.params.id)
    .reduce((obj, d) => {
      if (!obj.policyYear) return d;
      if (!d.policyYear) return obj;
      if (obj.policyYear < d.policyYear) obj = d;
      return obj;
    }, {} as CrimeBenchmark);

  if (!maxRecord.policyYear) {
    maxRecord.key = 'account';
    maxRecord.accountId = accountInfo.value.accountId;
    maxRecord.accountName = accountInfo.value.accountName;
    maxRecord.sic = accountInfo.value.sic || '';
    maxRecord.stockTicker = accountInfo.value.stockTicker || '';
    maxRecord.ownershipType = accountInfo.value.ownershipType || '';
    maxRecord.annualRevenue = accountInfo.value.annualRevenue;
    maxRecord.employeeCount = accountInfo.value.employeeCount;
  }
  return [{ ...maxRecord, frozen: true }] as unknown as CrimeBenchmark[];
});

const otherData = computed<CrimeBenchmark[]>(() => {
  if (appliedClients.value.length > 0) {
    return data.value
      .filter(d => appliedClients.value.includes(d.accountName || ''))
      .filter(filterByOwnershipType)
      .filter(filterByIndustry)
      .filter(filterByAnnualRevenue)
      .filter(filterByEmployeeCount)
      .map(transformAccountName) as CrimeBenchmark[];
  }
  return data.value
    .filter(d => d.accountId !== $route.params.id)
    .filter(filterByOwnershipType)
    .filter(filterByIndustry)
    .filter(filterByAnnualRevenue)
    .filter(filterByEmployeeCount)
    .map(transformAccountName) as CrimeBenchmark[];
});

const averageData = computed<CrimeBenchmark[]>(() => {
  return [{
    key: 'avg',
    accountName: ' ',
    policyYear: ' ' as never,
    programType: ' ',
    sic: '',
    industryName: 'Average',
    stockTicker: '',
    ownershipType: '',
    primaryCarrierGroup: '',
    primaryCarrierGroupID: '',
    policyEffectiveDate: '',
    annualRevenue: Math.round(calc(otherData.value, 'annualRevenue', calcAverage)),
    employeeCount: Math.round(calc(otherData.value, 'employeeCount', calcAverage)),
    totalRetention: calc(otherData.value, 'totalRetention', calcAverage, true),
    totalLimit: calc(otherData.value, 'totalLimit', calcAverage),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcAverage),
    maxPrimaryPPM: calc(otherData.value, 'maxPrimaryPPM', calcAverage),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcAverage),
    frozen: true,
  }];
});

const medianData = computed<CrimeBenchmark[]>(() => {
  return [{
    key: 'med',
    accountName: ' ',
    policyYear: ' ' as never,
    programType: ' ',
    sic: '',
    industryName: 'Median',
    stockTicker: '',
    ownershipType: '',
    primaryCarrierGroup: '',
    primaryCarrierGroupID: '',
    policyEffectiveDate: '',
    annualRevenue: calc(otherData.value, 'annualRevenue', calcMedian),
    employeeCount: calc(otherData.value, 'employeeCount', calcMedian),
    totalRetention: calc(otherData.value, 'totalRetention', calcMedian, true),
    totalLimit: calc(otherData.value, 'totalLimit', calcMedian),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcMedian),
    maxPrimaryPPM: calc(otherData.value, 'maxPrimaryPPM', calcMedian),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcMedian),
    frozen: true,
  }];
});

const isPublic = computed(() => props.account.ownershipType?.trim().toLowerCase() === PUBLIC);
const isPrivate = computed(() => !isPublic.value);

const columns = computed(() => {
  const columns: (Column<CrimeBenchmark> | undefined)[] = [
    { fieldName: 'policyYear', type: 'number', title: 'Year' },
    { fieldName: 'accountName', type: 'nosort', classes: 'truncated', title: 'Client', url: (data) => `/account/${data.accountId}/overview`, showInfoIcon: 'isPackaged', showInfoFieldName: 'packagedCoverageTypes' },
    { fieldName: 'industryName', type: '', title: 'Industry', url: (data) => `/account/${data.accountId}/edit`, accessoryIcon: 'pencil' },
    { fieldName: 'ownershipType', type: '', title: 'Ownership' },
    { fieldName: 'annualRevenue', type: 'compactMoney', title: 'Annual Revenue', filter: 'compactMoney', classes: isPrivate.value ? 'sorted' : '', exposureCategory: 'Company Financials', exposureMetricId: 1 },
    { fieldName: 'employeeCount', type: 'number', title: 'Employee Count', filter: 'integer', exposureCategory: 'Employee Count' },
    { fieldName: 'totalLimit', type: 'compactmoney', title: 'Total Limits', filter: 'compactMoney', url: (data) => `/account/${data.accountId}/policy-detail`, accessoryIcon: 'pencil' },
    { fieldName: 'totalRetention', type: 'compactmoney', title: 'Retention', filter: 'compactMoney' },
    { fieldName: 'totalPrimaryLimit', type: 'compactmoney', title: 'Primary Limit', filter: 'compactMoney' },
    { fieldName: 'maxPrimaryPPM', type: 'compactmoney', title: 'Primary PPM', filter: 'compactMoney' },
    { fieldName: 'firstLayerILF', type: 'text-right', title: '1st XS Rate', filter: 'percentage' },
    { fieldName: 'primaryCarrierGroup', type: '', title: 'Primary Carrier', url: (data) => `/carrier/${data.primaryCarrierGroupID}/overview` },
    { fieldName: 'policyEffectiveDate', type: 'date', title: 'Effective Date', filter: 'date', useFilteredExport: true }
  ];
  return columns.filter(x => x) as Column<CrimeBenchmark>[];
});

const charts = computed(() => {
  const charts: (Chart<CrimeBenchmark> | undefined)[] = [
    { fieldName: 'employeeCount', title: 'Employee Count' },
    { fieldName: 'totalLimit', title: 'Total Limits' },
    { fieldName: 'totalRetention', title: 'Retention' },
    { fieldName: 'totalPrimaryLimit', title: 'Primary Limit' },
    { fieldName: 'maxPrimaryPPM', title: 'Primary PPM' },
    { fieldName: 'firstLayerILF', title: '1st XS Rate', isPercentage: true },
  ];
  return charts.filter(x => x) as Chart<CrimeBenchmark>[];
});

const maxAnnualRevenue = computed(() => {
  const vals = data.value.map(d => d.annualRevenue).filter(d => !!d) as number[];
  return vals.length === 0 ? 0 : Math.max(...vals);
});

const maxEmployeeCount = computed(() => {
  const vals = data.value.map(d => d.employeeCount).filter(d => !!d) as number[];
  return vals.length === 0 ? 0 : Math.max(...vals);
});

const selectedIndustriesSet = computed(() => {
  return selectedIndustries.value.reduce((acc, i) => {
    acc.add(i)
    return acc;
  }, new Set<number>());
});

watch(selectedAttributeTypes, async () => {
  await refresh();
});

watch(otherData, () => {
  // the apptable sorting interferes with reactive table data... this forces a re-render
  benchmarkTableKey.value += 1;
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  await refresh();
});

// ----------
// functions
// ----------
function transformAccountName(d: CrimeBenchmark): CrimeBenchmark {
  return ({ ...d, accountName: appliedLabelDisplay.value === SHOW_ALL ? d.accountName : d.accountNameAlt });
}

function applyClientFilter(): void {
  appliedClients.value = [...selectedClients.value];
}

function applyViewOptions(selectedLabels: string): void {
  appliedLabelDisplay.value = selectedLabels;
}

function applyClientAttributesFilter(attrIds: (number | string)[]): void {
  selectedAttributeTypes.value = attrIds as number[];
}

async function refresh() {
  isLoading.value = true;
  const [dataResult, attributeTypesResult, account] = await Promise.all([
    benchmarkService.crime(selectedAttributeTypes.value),
    referenceService.accountAttributeTypes(),
    benchmarkService.account($route.params.id as string),
  ]);

  accountInfo.value = account;
  attributeTypesResult.sort((a, b) => a.name.localeCompare(b.name));
  attributeTypes.value = attributeTypesResult
    .filter(t => sameOwnershipType(t, props.account.ownershipType) || both(t))
    .map(t => ({ label: t.name, value: t.id }));
  data.value = (dataResult || []).map((d, i) => ({ ...d, packagedCoverageTypes: `Packaged with ${d.packagedCoverageTypes}`, accountNameAlt: `Company ${i + 1}` }));

  // populate industries filter

  if (industries.value.length === 0) {
    industries.value = Array.from(data.value.reduce((acc, d) => {
      if (d.industryId && d.industryName) {
        acc.set(d.industryId, { label: d.industryName, value: d.industryId });
      }
      return acc;
    }, new Map<number, LabelFilterType<number>>())
      .values())
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  isLoading.value = false;
}

function filterByAnnualRevenue(d: CrimeBenchmark): boolean {
  if (annualRevenueRange.value) {
    const min = annualRevenueRange.value[0];
    const max = annualRevenueRange.value[1];
    if (!d.annualRevenue) return false;
    return d.annualRevenue >= min && d.annualRevenue <= max;
  }
  return true;
}

function filterByEmployeeCount(d: CrimeBenchmark): boolean {
  if (employeeCountRange.value) {
    const min = employeeCountRange.value[0];
    const max = employeeCountRange.value[1];
    if (!d.employeeCount) return false;
    return d.employeeCount >= min && d.employeeCount <= max;
  }
  return true;
}

function filterByOwnershipType(d: CrimeBenchmark): boolean {
  if (!ownershipTypeFilter.showPublic && !ownershipTypeFilter.showPrivate) return true; // no filter is set

  switch(d.ownershipType) {
    case 'Public':
      return ownershipTypeFilter.showPublic;
    case 'Private':
      return ownershipTypeFilter.showPrivate;
    default:
      return false;
  }
}

function filterByIndustry(d: CrimeBenchmark): boolean {
  if (selectedIndustries.value.length === 0) return true;
  if (!d.industryId) return false;
  return selectedIndustriesSet.value.has(d.industryId);
}

function employeeCountChanged(range: number[]) {
  employeeCountRange.value = range.length === 2 ? [range[0], range[1]] : undefined;
}

function annualRevenueChanged(range: number[]) {
  annualRevenueRange.value = range.length === 2 ? [range[0] * 1_000_000, range[1] * 1_000_000] : undefined;
}
</script>

<template>
  <BenchmarkingTable titleText="Crime Benchmarking" :key="benchmarkTableKey"
                     :accountData="accountData" :otherData="otherData as Benchmark[]"
                     :averageData="averageData as Benchmark[]" :medianData="medianData as Benchmark[]"
                     :columns="columns as Column[]" :loading="isLoading"
                     @needs-update="emit('needs-update')"
  >
    <template v-slot:charts>
      <BenchmarkingCharts :accountData="accountData as Benchmark[]" :otherData="otherData as Benchmark[]" :charts="charts as Chart[]"/>
    </template>

    <template v-slot:filterbar>
      <slot name="filterbar"/>

      <ClientFilter v-model="selectedClients" :items="clients" @update:model-value="applyClientFilter"/>

      <FilterSlider title="Annual Revenue" description="Filter clients with annual revenue between these values."
                    :values="[0, Math.ceil(maxAnnualRevenue / 1000000)]"
                    :value-formatter-fn="(val) => val ? compactMoney(val * 1000000) : ''"
                    min-title="Min (Millions)" max-title="Max (Millions)"
                    @update:model-value="annualRevenueChanged" data-testid="plan-assets-filter-activator" dataTestPrefix="plan-assets"
      />

      <FilterSlider title="Employee Count" description="Filter clients with employee counts between these values."
                    :values="[0, maxEmployeeCount]"
                    numeric-values
                    min-title="Min" max-title="Max"
                    @update:model-value="employeeCountChanged"
      />

      <GenericFilter :types="attributeTypes" chip-title-field="label" v-model="selectedAttributeTypes"
                     title="Client Attributes" @update:model-value="applyClientAttributesFilter"/>

      <OwnershipTypeFilter v-model:show-private="ownershipTypeFilter.showPrivate"
                           v-model:show-public="ownershipTypeFilter.showPublic"
      />

      <GenericFilter
        title="Industry" chip-title-field="label"
        :types="industries"
        v-model="selectedIndustries"
      />

      <AccountBenchmarkViewOptions :labels="appliedLabelDisplay" @change="applyViewOptions" />
    </template>
  </BenchmarkingTable>
</template>

